// График остатка товара на конец дня — линия. Своя разметка без библиотек (как KitchenChart): линия и заливка —
// встроенный SVG, точки, нулевая линия и подписи — HTML поверх (не растягиваются вместе с SVG, у точек подсказки).
// - Шкала Y — по диапазону остатков с запасом, не от нуля (stockModel.lineModel). Нулевая линия — пунктиром с
//   подписью «0», только если ноль в диапазоне.
// - Выше нуля — серая линия с лёгкой серой заливкой до нулевой линии (до низа, если ноль ниже диапазона), ниже — красная
//   с лёгкой красной заливкой; весь период в минусе — красная линия без заливки. Пересечение нуля вниз в день ухода
//   в минус — красная точка на нулевой линии.
// - Даты под осью: дни с минусовым остатком — красным. Подсказка у точки — наведение на десктопе, нажатие на телефоне:
//   «24.10: остаток +3,54 кг, за день −8,977 кг». Синий на графике не используем.
import React from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { MUTED_TEXT_COLOR } from '../../../../styles/tokens';
import { dayLabel, formatChange, lineModel } from '../../utils/stockModel';

export const NEGATIVE_COLOR = '#ef6b6b';
export const POSITIVE_COLOR = '#66bb6a';
const PLUS_LINE = '#a1a1aa';
const PLUS_FILL = 'rgba(161,161,170,0.12)';
const MINUS_FILL = 'rgba(239,107,107,0.14)';
const HEIGHT = 140;
const TOUCH_HOLD_MS = 2500;

// «24.10: остаток +3,54 кг, за день −8,977 кг»
export function dayHint(day, unit, went) {
  return `${dayLabel(day.date)}: остаток ${formatChange(day.balance, unit)}, за день ${formatChange(day.change, unit)}${went ? ', ушёл в минус' : ''}`;
}

const pointsAttr = (points) => points.map(([x, y]) => `${x},${y}`).join(' ');

export default function StockHistoryChart({ days, wentNegative, unit }) {
  const { zero, points, segments, crossings } = lineModel(days.map((day) => day.balance));
  const wentIndex = days.findIndex((day) => day.date === wentNegative);
  // Точка ухода в минус: пересечение нуля перед днём ухода; ушёл в первый же день периода — сама точка дня
  const wentCross = crossings.find((c) => c.toNegative && c.index === wentIndex) || null;

  return (
    <Box role="group" aria-label="Остаток на конец дня" sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box sx={{ position: 'relative', height: HEIGHT }}>
        <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
          {segments.map((segment, index) => {
            // Заливка до нулевой линии; ноль ниже диапазона (всё в плюсе) — до низа; весь минус без нуля — без заливки
            const base = zero ?? (segment.negative ? null : 100);
            const first = segment.points[0];
            const last = segment.points[segment.points.length - 1];
            return (
              <g key={index}>
                {base !== null && segment.points.length > 1 && (
                  <polygon
                    points={pointsAttr([...segment.points, [last[0], base], [first[0], base]])}
                    fill={segment.negative ? MINUS_FILL : PLUS_FILL}
                  />
                )}
                <polyline
                  points={pointsAttr(segment.points)}
                  fill="none"
                  stroke={segment.negative ? NEGATIVE_COLOR : PLUS_LINE}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            );
          })}
        </Box>

        {zero !== null && (
          <>
            <Box aria-hidden="true" sx={{ position: 'absolute', left: 0, right: 0, top: `${zero}%`, borderTop: '1px dashed rgba(255,255,255,0.3)' }} />
            <Typography component="span" aria-hidden="true" sx={{ position: 'absolute', left: 0, top: `${zero}%`, transform: 'translateY(-110%)', fontSize: '11px', color: MUTED_TEXT_COLOR, lineHeight: 1 }}>
              0
            </Typography>
          </>
        )}

        {wentCross && (
          <Box
            aria-hidden="true"
            data-went-negative
            sx={{ position: 'absolute', left: `${wentCross.x}%`, top: `${wentCross.y}%`, width: 9, height: 9, ml: '-4.5px', mt: '-4.5px', borderRadius: '50%', backgroundColor: NEGATIVE_COLOR, boxShadow: '0 0 0 2px #1c1c1f' }}
          />
        )}

        {points.map((point, index) => {
          const day = days[index];
          const went = index === wentIndex;
          const text = dayHint(day, unit, went);
          // Ушёл в минус в первый же день периода (пересечения на графике нет) — отмечена сама точка дня
          const mark = went && !wentCross;
          return (
            <Tooltip key={day.date} title={text} placement="top" arrow enterTouchDelay={0} leaveTouchDelay={TOUCH_HOLD_MS}>
              <ButtonBase
                aria-label={text}
                data-went-negative={mark || undefined}
                disableRipple
                sx={{
                  position: 'absolute',
                  left: `${point.x}%`,
                  top: `${point.y}%`,
                  width: 24,
                  height: 24,
                  ml: '-12px',
                  mt: '-12px',
                  borderRadius: '50%',
                  '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: '1px' },
                  '@media (hover: hover)': { '&:hover > span': { transform: 'scale(1.35)' } },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: mark ? 9 : 6,
                    height: mark ? 9 : 6,
                    borderRadius: '50%',
                    backgroundColor: point.negative ? NEGATIVE_COLOR : PLUS_LINE,
                    boxShadow: '0 0 0 2px #1c1c1f',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>

      <Box aria-hidden="true" sx={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
        {days.map((day) => {
          const minus = Number(day.balance) < 0;
          return (
            <Typography
              key={day.date}
              component="span"
              data-minus={minus || undefined}
              sx={{ textAlign: 'center', fontSize: '11px', fontVariantNumeric: 'tabular-nums', color: minus ? NEGATIVE_COLOR : MUTED_TEXT_COLOR, fontWeight: day.date === wentNegative ? 700 : 400 }}
            >
              {dayLabel(day.date)}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
}
